import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    // Report render crashes (inert unless a Sentry DSN is configured).
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack ?? '' },
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="Oops! Something went wrong"
            subTitle="We're sorry for the inconvenience. Please try refreshing the page."
            extra={[
              <Button key="retry" onClick={this.handleReset}>
                Try Again
              </Button>,
              <Button key="home" type="primary" onClick={() => (window.location.href = '/')}>
                Go Home
              </Button>,
            ]}
          >
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-4 overflow-auto rounded bg-gray-100 p-4 text-xs text-left">
                {this.state.error.message}
              </pre>
            )}
          </Result>
        </div>
      );
    }

    return this.props.children;
  }
}
